import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchBlocks,
  fetchPapers,
  fetchBlockImages,
  fetchBlockLists,
  deleteImage,
  deleteList,
  updateBlock,
  updateImage,
  updateList,
  createList,
  uploadImage,
  replaceImage,
  replaceBlockOriginal,
  reorderBlockImages,
  reorderBlockLists,
} from "../../api/adminApi";
import { useToast } from "../../context/ToastContext";
import { useConfirm } from "../../context/ConfirmContext";
import * as S from "./styles";
import BlockFields from "./components/BlockFields";
import ImagesSection from "./components/ImagesSection";
import ImageModal from "./components/ImageModal";

const BlockEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const blockId = Number(id);
  const toast = useToast();
  const confirm = useConfirm();

  // Block fields state
  const [block, setBlock] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    credential: "",
    original: "",
    text: "",
    color: "",
    paperId: "",
  });
  const [papers, setPapers] = useState([]);
  const [blocks, setBlocks] = useState([]);

  // Images state
  const [blockImages, setBlockImages] = useState([]);
  const [imageTitles, setImageTitles] = useState({});
  const [showAddImageModal, setShowAddImageModal] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  // Lists state
  const [blockLists, setBlockLists] = useState([]);
  const [listTitles, setListTitles] = useState({});
  const [showAddListModal, setShowAddListModal] = useState(false);
  const [newListTitle, setNewListTitle] = useState("");
  const [listIdChanged, setListIdChanged] = useState("");

  // Drag and drop state
  const [draggedImage, setDraggedImage] = useState(null);
  const [draggedList, setDraggedList] = useState(null);
  const [activeImageDropZone, setActiveImageDropZone] = useState(null);
  const [activeListDropZone, setActiveListDropZone] = useState(null);

  // General state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Replace image dialog state
  const [replacingImageId, setReplacingImageId] = useState(null);
  const [uploadingReplace, setUploadingReplace] = useState(false);
  const replaceFileInputRef = useRef(null);

  // Replace block original dialog state
  const [replacingOriginal, setReplacingOriginal] = useState(false);
  const [uploadingOriginal, setUploadingOriginal] = useState(false);
  const originalFileInputRef = useRef(null);

  const showMessage = (text, type) => {
    if (type === "error") {
      toast.error(text);
    } else {
      toast.success(text);
    }
  };

  // Load all data on mount
  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [blocksData, papersData, bi, bl] = await Promise.all([
        fetchBlocks(),
        fetchPapers(),
        fetchBlockImages(blockId),
        fetchBlockLists(blockId),
      ]);

      const foundBlock = blocksData.find((b) => b.id === blockId);
      if (!foundBlock) {
        toast.error("Блок не найден");
        navigate("/opredelitel/admin/blocks");
        return;
      }

      setBlock(foundBlock);
      setFormData({
        title: foundBlock.title || "",
        credential: foundBlock.credential || "",
        original: foundBlock.original || "",
        text: foundBlock.text || "",
        color: foundBlock.color || "",
        paperId: foundBlock.paper_id || "",
      });
      setPapers(papersData);
      setBlockImages(bi);
      setBlockLists(bl);
      setBlocks(blocksData);

      // Initialize title maps
      const iTitles = {};
      bi.forEach((r) => {
        if (r.title) iTitles[r.image_id] = r.title;
      });
      setImageTitles(iTitles);

      const lTitles = {};
      bl.forEach((r) => {
        if (r.title) lTitles[r.list_id] = r.title;
      });
      setListTitles(lTitles);
    } catch (err) {
      toast.error("Ошибка загрузки данных");
    } finally {
      setLoading(false);
    }
  };

  // Save block fields
  const handleSaveBlock = async () => {
    setSaving(true);
    try {
      await updateBlock(blockId, {
        title: formData.title,
        credential: formData.credential,
        original: formData.original || firstImage,
        text: formData.text,
        color: formData.color,
        paperId: Number(formData.paperId),
      });
      toast.success("Блок сохранён");
    } catch (err) {
      toast.error("Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  // ==================== Image Management ====================

  const handleThumbnailClick = (imageId) => {
    setReplacingImageId(imageId);
  };

  const handleReplaceFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || replacingImageId === null) return;
    setUploadingReplace(true);
    try {
      await replaceImage(replacingImageId, file);
      toast.success("Изображение заменено");

      // Reload images to get updated data
      const [bi] = await Promise.all([fetchBlockImages(blockId)]);
      setBlockImages(bi);
      setReplacingImageId(null);
    } catch (err) {
      toast.error("Ошибка замены изображения");
    } finally {
      setUploadingReplace(false);
      if (replaceFileInputRef.current) replaceFileInputRef.current.value = "";
    }
  };

  // ==================== Block Original Management ====================

  const handleOriginalClick = () => {
    if (!formData.original) {
      toast.error("Нет текущего изображения для замены");
      return;
    }
    setReplacingOriginal(true);
  };

  const handleOriginalFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingOriginal(true);
    try {
      const updated = await replaceBlockOriginal(blockId, file);
      toast.success("Оригинальное изображение заменено");

      setBlock(updated);
      setFormData((prev) => ({ ...prev, original: updated.original }));
      setReplacingOriginal(false);
    } catch (err) {
      toast.error("Ошибка замены оригинального изображения");
    } finally {
      setUploadingOriginal(false);
      if (originalFileInputRef.current) originalFileInputRef.current.value = "";
    }
  };

  const handleImageTitleChange = (imageId, title) => {
    setImageTitles((prev) => ({ ...prev, [imageId]: title }));
  };

  const handleImageTitleBlur = async (imageId, title) => {
    try {
      await updateImage(imageId, { title });
      toast.success("Заголовок обновлён");
    } catch (err) {
      toast.error("Ошибка обновления заголовка");
    }
  };

  const handleDeleteImage = async (imageId) => {
    const confirmed = await confirm({
      title: "Удалить изображение?",
      message: "Изображение будет удалено.",
    });
    if (!confirmed) return;
    try {
      await deleteImage(imageId);
      const remainingImages = blockImages
        .filter((r) => r.image_id !== imageId)
        .map((r, idx) => ({ ...r, display_order: idx }));
      setBlockImages(remainingImages);
      toast.success("Изображение удалено");
    } catch (err) {
      toast.error("Ошибка удаления");
    }
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      // Upload image and link to block in one request with shared change_group_id
      await uploadImage(file, "", blockId);
      setShowAddImageModal(false);
      toast.success("Изображение добавлено");

      // Reload images
      const [bi] = await Promise.all([fetchBlockImages(blockId)]);
      setBlockImages(bi);
    } catch (err) {
      toast.error("Ошибка загрузки");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Drag and drop for images
  const handleImageDragStart = (e, imageId) => {
    setDraggedImage(imageId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleImageZoneDragOver = (e, zoneIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (activeImageDropZone !== zoneIndex) {
      setActiveImageDropZone(zoneIndex);
    }
  };

  const handleImageZoneDragLeave = (zoneIndex) => {
    if (activeImageDropZone === zoneIndex) {
      setActiveImageDropZone(null);
    }
  };

  const handleImageZoneDrop = async (e, targetZoneIndex) => {
    e.preventDefault();
    setActiveImageDropZone(null);

    if (draggedImage === null) return;

    const currentDragged = draggedImage;
    setDraggedImage(null);

    const draggedIndex = blockImages.findIndex(
      (r) => r.image_id === currentDragged,
    );

    if (
      draggedIndex === -1 ||
      targetZoneIndex === draggedIndex ||
      targetZoneIndex === draggedIndex + 1
    ) {
      return;
    }

    const newOrder = [...blockImages];
    const [draggedItem] = newOrder.splice(draggedIndex, 1);
    const newIndex =
      targetZoneIndex > draggedIndex ? targetZoneIndex - 1 : targetZoneIndex;
    newOrder.splice(newIndex, 0, draggedItem);

    // Update display_order for all items
    const updatedImages = newOrder.map((r, idx) => ({
      ...r,
      display_order: idx,
    }));

    const imageOrders = updatedImages.map((r) => ({
      imageId: r.image_id,
      displayOrder: r.display_order,
    }));

    try {
      await reorderBlockImages(blockId, imageOrders);
      setBlockImages(updatedImages);
      toast.success("Порядок обновлён");
    } catch (err) {
      toast.error("Ошибка обновления порядка");
    }
  };

  const handleImageDragEnd = () => {
    setDraggedImage(null);
    setActiveImageDropZone(null);
  };

  // ==================== List Management ====================

  const handleListTitleChange = (listId, title) => {
    if (listTitles[listId] !== title) setListIdChanged(listId);
    setListTitles((prev) => ({ ...prev, [listId]: title }));
  };

  const handleListTitleBlur = async (listId, title) => {
    if (listIdChanged !== listId) return;

    setListIdChanged("");

    try {
      await updateList(listId, { title });
      toast.success("Заголовок обновлён");
    } catch (err) {
      toast.error("Ошибка обновления заголовка");
    }
  };

  const handleDeleteList = async (listId) => {
    const confirmed = await confirm({
      title: "Удалить список?",
      message: "Список будет удалён.",
    });
    if (!confirmed) return;
    try {
      await deleteList(listId);
      const remainingLists = blockLists
        .filter((r) => r.list_id !== listId)
        .map((r, idx) => ({ ...r, display_order: idx }));
      setBlockLists(remainingLists);
      toast.success("Список удалён");
    } catch (err) {
      toast.error("Ошибка удаления");
    }
  };

  const handleCreateNewList = async () => {
    if (!newListTitle.trim()) {
      toast.error("Введите заголовок");
      return;
    }
    try {
      // Create list and link to block in one request with shared change_group_id
      const newList = await createList({
        title: newListTitle.trim(),
        blockId,
      });
      setShowAddListModal(false);
      setNewListTitle("");
      toast.success("Список добавлен");

      const [bl] = await Promise.all([fetchBlockLists(blockId)]);
      setBlockLists(bl);
      setListTitles((prev) => ({ ...prev, [newList.id]: newList.title }));
    } catch (err) {
      toast.error("Ошибка добавления");
    }
  };

  // Drag and drop for lists
  const handleListDragStart = (e, listId) => {
    setDraggedList(listId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleListZoneDragOver = (e, zoneIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (activeListDropZone !== zoneIndex) {
      setActiveListDropZone(zoneIndex);
    }
  };

  const handleListZoneDragLeave = (zoneIndex) => {
    if (activeListDropZone === zoneIndex) {
      setActiveListDropZone(null);
    }
  };

  const handleListZoneDrop = async (e, targetZoneIndex) => {
    e.preventDefault();
    setActiveListDropZone(null);

    if (draggedList === null) return;

    const currentDragged = draggedList;
    setDraggedList(null);

    const draggedIndex = blockLists.findIndex(
      (r) => r.list_id === currentDragged,
    );

    if (
      draggedIndex === -1 ||
      targetZoneIndex === draggedIndex ||
      targetZoneIndex === draggedIndex + 1
    ) {
      return;
    }

    const newOrder = [...blockLists];
    const [draggedItem] = newOrder.splice(draggedIndex, 1);
    const newIndex =
      targetZoneIndex > draggedIndex ? targetZoneIndex - 1 : targetZoneIndex;
    newOrder.splice(newIndex, 0, draggedItem);

    const updatedLists = newOrder.map((r, idx) => ({
      ...r,
      display_order: idx,
    }));

    const listOrders = updatedLists.map((r) => ({
      listId: r.list_id,
      displayOrder: r.display_order,
    }));

    try {
      await reorderBlockLists(blockId, listOrders);
      setBlockLists(updatedLists);
      toast.success("Порядок обновлён");
    } catch (err) {
      toast.error("Ошибка обновления порядка");
    }
  };

  const handleListDragEnd = () => {
    setDraggedList(null);
    setActiveListDropZone(null);
  };

  // ==================== Loading State ====================

  if (loading) return <S.Loading>Загрузка...</S.Loading>;
  if (!block) return <S.Loading>Блок не найден</S.Loading>;

  const firstImage = blockImages?.find(
    (image) => image.display_order === 0,
  )?.original;

  // ==================== Render ====================

  return (
    <S.Container>
      <S.Header>
        <S.PageTitle
          as="button"
          onClick={() => navigate("/opredelitel/admin/blocks")}
        >
          Узоры
        </S.PageTitle>
        -<S.PageTitle>{block.title}</S.PageTitle>
      </S.Header>

      {/* Block Fields Section */}
      <BlockFields
        formData={formData}
        papers={papers}
        blocks={blocks}
        firstImage={firstImage}
        setFormData={setFormData}
        handleSaveBlock={handleSaveBlock}
        handleOriginalClick={handleOriginalClick}
        saving={saving}
        showMessage={showMessage}
        blockLists={blockLists}
        listTitles={listTitles}
        draggedList={draggedList}
        activeListDropZone={activeListDropZone}
        newListTitle={newListTitle}
        showAddListModal={showAddListModal}
        handleListTitleChange={handleListTitleChange}
        handleListTitleBlur={handleListTitleBlur}
        handleDeleteList={handleDeleteList}
        handleListDragStart={handleListDragStart}
        handleListDragEnd={handleListDragEnd}
        handleListZoneDragOver={handleListZoneDragOver}
        handleListZoneDragLeave={handleListZoneDragLeave}
        handleListZoneDrop={handleListZoneDrop}
        setNewListTitle={setNewListTitle}
        setShowAddListModal={setShowAddListModal}
        handleCreateNewList={handleCreateNewList}
      />

      {/* Images Section */}
      <ImagesSection
        blockImages={blockImages}
        imageTitles={imageTitles}
        draggedImage={draggedImage}
        activeImageDropZone={activeImageDropZone}
        handleThumbnailClick={handleThumbnailClick}
        handleImageTitleChange={handleImageTitleChange}
        handleImageTitleBlur={handleImageTitleBlur}
        handleDeleteImage={handleDeleteImage}
        handleImageDragStart={handleImageDragStart}
        handleImageDragEnd={handleImageDragEnd}
        handleImageZoneDragOver={handleImageZoneDragOver}
        handleImageZoneDragLeave={handleImageZoneDragLeave}
        handleImageZoneDrop={handleImageZoneDrop}
        showAddImageModal={showAddImageModal}
        setShowAddImageModal={setShowAddImageModal}
        replacingImageId={replacingImageId}
        uploadingReplace={uploadingReplace}
        replaceFileInputRef={replaceFileInputRef}
        uploadingImage={uploadingImage}
        fileInputRef={fileInputRef}
      />

      {/* Image Modals */}
      <ImageModal
        showAddImageModal={showAddImageModal}
        setShowAddImageModal={setShowAddImageModal}
        uploadingImage={uploadingImage}
        fileInputRef={fileInputRef}
        handleUploadFile={handleUploadFile}
        replacingImageId={replacingImageId}
        setReplacingImageId={setReplacingImageId}
        uploadingReplace={uploadingReplace}
        replaceFileInputRef={replaceFileInputRef}
        handleReplaceFileSelect={handleReplaceFileSelect}
        replacingOriginal={replacingOriginal}
        setReplacingOriginal={setReplacingOriginal}
        uploadingOriginal={uploadingOriginal}
        originalFileInputRef={originalFileInputRef}
        handleOriginalFileSelect={handleOriginalFileSelect}
      />
    </S.Container>
  );
};

export default BlockEditor;
